import React, { type ReactNode, type UIEvent, useCallback, useEffect, useRef } from 'react';

import { useAppSelector } from '@fd/customHooks/useAppSelector';
import { Plus } from '@flipdish/ui-icons';
import { Button } from '@flipdish/ui-library/components/atoms/Button';
import { Divider } from '@flipdish/ui-library/components/atoms/Divider';
import { LoadingIndicator } from '@flipdish/ui-library/components/atoms/LoadingIndicator';
import { SearchInput } from '@flipdish/ui-library/components/atoms/SearchInput';
import { SidePanel } from '@flipdish/ui-library/components/organisms/SidePanel';
import { getTranslate } from 'react-localize-redux';
import Permissions from 'react-redux-permissions';

import { type SwitcherItem, useOrganisationSwitcher } from './hooks/useOrganisationSwitcher';
import { OrganisationRow } from './OrganisationRow';

type Props = {
  /** Returns to the organisation summary. */
  onBack: () => void;
  /** Called once the user has chosen an organisation, so the shell can show its summary. */
  onOrganisationSwitched: () => void;
  /** Called once the user has chosen a brand (`Brand:` search), so the shell can close a covering panel. */
  onBrandSwitched: () => void;
  /** "Add organisation" was pressed; the shell closes the panel and navigates. */
  onAddOrganisation: () => void;
};

type SectionProps = {
  title: string;
  /** Rendered after the title, e.g. a loading indicator. */
  action?: ReactNode;
  children: ReactNode;
  'data-fd'?: string;
};

const Section = ({ title, action, children, 'data-fd': dataFd }: SectionProps) => (
  <section className="flex flex-col gap-5" data-fd={dataFd}>
    <div className="flex items-center justify-between gap-2">
      <h3 className="text-md font-semibold text-primary">{title}</h3>
      {action}
    </div>
    {children}
  </section>
);

/**
 * The org switcher as `SidePanel` content: search, recently viewed and every organisation. A
 * search starting with `Brand:` lists brands across every organisation instead.
 */
export const SwitchOrganisationPanel = ({
  onBack,
  onOrganisationSwitched,
  onBrandSwitched,
  onAddOrganisation,
}: Props) => {
  const translate = useAppSelector((state) => getTranslate(state.locale));
  const scrollRef = useRef<HTMLDivElement>(null);
  const {
    search,
    searchTerm,
    brandMode,
    brandTerm,
    handleSearch,
    items,
    recentItems,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    isSwitching,
    selectItem,
  } = useOrganisationSwitcher({ onOrganisationSwitched, onBrandSwitched });

  const loadMoreIfNeeded = useCallback(
    (element: HTMLDivElement | null) => {
      if (!element || !hasNextPage || isFetchingNextPage) return;
      const { scrollHeight, clientHeight, scrollTop } = element;
      const nearBottom = Math.ceil(scrollTop + clientHeight) >= scrollHeight - clientHeight / 2;
      if (nearBottom) fetchNextPage();
    },
    [hasNextPage, isFetchingNextPage, fetchNextPage]
  );

  const handleScroll = ({ currentTarget }: UIEvent<HTMLDivElement>) =>
    loadMoreIfNeeded(currentTarget);

  // A page that does not overflow never scrolls, so keep loading until it does or the list ends.
  useEffect(() => {
    loadMoreIfNeeded(scrollRef.current);
  }, [items.length, loadMoreIfNeeded]);

  const selectLabelKey = brandMode ? 'Switch_to_brand' : 'Switch_to_organisation';
  const copyLabel = translate(brandMode ? 'Copy_brand_ID' : 'Copy_organisation_ID') as string;
  const copiedLabel = translate(brandMode ? 'Brand_ID_copied' : 'Organisation_ID_copied') as string;

  const renderRows = (rows: SwitcherItem[], prefix: string) => (
    <ul className="flex flex-col gap-4">
      {/* The API can return an org twice and some with no id; the pages only append, so the position keeps the key unique and stable. */}
      {rows.map((item, index) => (
        <OrganisationRow
          key={`${item.id}:${index}`}
          id={item.id}
          name={item.name}
          logoImageUrl={item.logoImageUrl}
          isArchived={item.isArchived}
          isDisabled={isSwitching}
          onSelect={(openInNewTab) => selectItem(item, openInNewTab)}
          selectLabel={translate(selectLabelKey, { name: item.name }) as string}
          copyLabel={copyLabel}
          copiedLabel={copiedLabel}
          data-fd={`${prefix}-${item.id}`}
        />
      ))}
    </ul>
  );

  const emptyMessage = () => {
    if (brandMode) return translate('No_brands_found_for_{searchTerm}', { searchTerm: brandTerm });
    // The org the user is in is left out, so with one org and no search there is nothing to list.
    if (!searchTerm) return translate('No_other_organisations');
    return translate('No_orgs_found_for', { searchTerm });
  };

  const renderItems = () => {
    if (isLoading) {
      return (
        <div className="flex justify-center py-6">
          <LoadingIndicator size="md" />
        </div>
      );
    }
    if (items.length === 0) {
      return (
        <p className="text-sm text-tertiary" data-fd="switch-organisation-empty">
          {emptyMessage() as string}
        </p>
      );
    }
    return renderRows(items, 'switch-organisation-item');
  };

  return (
    <>
      <SidePanel.Header onBack={onBack} backLabel={translate('Back') as string}>
        <h2 className="truncate pr-14 text-lg font-semibold text-primary">
          {translate('Switch_organisation') as string}
        </h2>
      </SidePanel.Header>
      {/* `SidePanel.Content` does not forward refs, so the scrolling (and overflow check) moves to a div of our own. */}
      <SidePanel.Content className="overflow-hidden pb-0" data-fd="switch-organisation-panel">
        <div
          ref={scrollRef}
          onScroll={handleScroll}
          className="flex min-h-0 flex-1 flex-col gap-6 overflow-y-auto pb-6"
          data-fd="switch-organisation-scroll"
        >
          <div className="flex flex-col gap-6">
            <SearchInput
              aria-label={translate('Search_organisation') as string}
              placeholder={translate('Search_organisation') as string}
              value={search}
              onChange={handleSearch}
              autoFocus
            />
            <Divider />
          </div>
          {!brandMode && recentItems.length > 0 && (
            <>
              <Section
                title={translate('Recently_viewed') as string}
                data-fd="switch-organisation-recent"
              >
                {renderRows(recentItems, 'switch-organisation-recent-item')}
              </Section>
              <Divider />
            </>
          )}
          {/* The rows stay while a switch is in flight, disabled, so the pressed one keeps focus if the switch fails. */}
          <Section
            title={translate(brandMode ? 'Brands' : 'All_organisations') as string}
            action={isSwitching ? <LoadingIndicator size="sm" /> : undefined}
            data-fd="switch-organisation-all"
          >
            {renderItems()}
            {isFetchingNextPage && (
              <div className="flex justify-center py-2">
                <LoadingIndicator size="sm" />
              </div>
            )}
          </Section>
        </div>
      </SidePanel.Content>
      <Permissions allowed={['FlipdishStaff']}>
        <SidePanel.Footer variant="actions">
          <Button
            color="primary"
            size="md"
            iconLeading={Plus}
            onPress={onAddOrganisation}
            data-fd="admin-only-add-org"
          >
            {translate('Add_organisation') as string}
          </Button>
        </SidePanel.Footer>
      </Permissions>
    </>
  );
};

export default SwitchOrganisationPanel;
